import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';

import { SampleFilter } from 'src/shared/types';
import { statisticalComparisonQuery, StatisticalComparisonQueryResult } from './queries/statisticalComparisonQuery';
import { speciesTrendsQuery, SpeciesTrendsQueryResult } from './queries/speciesTrendsQuery';
import { taxonomicComparisonQuery, TaxonomicComparisonQueryResult } from './queries/taxonomicComparisonQuery';
import {
    communityComparisonChartDataQuery,
    CommunityComparisonChartDataQueryResult,
    communityComparisonSamplesQuery,
    CommunityComparisonSamplesQueryResult,
} from './queries/communityComparisonQuery';
import { communityComparisonMarkerQuery, communityComparisonMarkersQueryResult } from './queries/communityComparisonMarkerQuery';
import { sampleComparisonQuery, SampleComparisonQueryResult } from './queries/sampleComparisonQuery';
import { habitatTrendsQuery, HabitatTrendsQueryResult } from './queries/habitatTrendsQuery';

type GenericQueryParems = {
    projectId: string;
    habitatAssay: string;
    fromDate?: string;
    toDate?: string;
};

type EcologyHubQueryParams = {
    projectId: string;
    chartId: string;
    habitatAssay: string;
};

type TaxonomicComparisonQueryParams = {
    projectId: string;
    samples: SampleFilter;
};

type CommunityComparisonSamplesQueryParams = {
    projectId: string;
    samples: SampleFilter;
};

type CommunityComparisonMarkerQueryParams = {
    projectId: string;
    sampleId: string;
};

type SampleComparisonQueryParams = {
    projectId: string;
    samples: SampleFilter;
};

type HabitatTrendsQueryParams = {
    projectId: string;
    habitatInsightsCharts: string[];
    siteName: string[];
};

const ecologyHubGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        statisticalComparison: builder.query<StatisticalComparisonQueryResult, GenericQueryParems>({
            query: params => ({
                body: statisticalComparisonQuery,
                variables: params,
            }),
        }),
        speciesTrends: builder.query<SpeciesTrendsQueryResult, EcologyHubQueryParams>({
            query: params => ({
                body: speciesTrendsQuery,
                variables: params,
            }),
            transformResponse: (response: SpeciesTrendsQueryResult) => {
                if (!response?.speciesTrends?.data) {
                    return response;
                }

                //Flatten speciesTrends data to support multi year events
                const multiYearData = response.speciesTrends.data
                    .flatMap(item => item.data)
                    .sort((a, b) => Number(a.datetime) - Number(b.datetime));

                return {
                    speciesTrends: {
                        ...response.speciesTrends,
                        data: [
                            {
                                metric: response.speciesTrends.data[0].metric,
                                data: multiYearData,
                            },
                        ],
                    },
                };
            },
        }),
        taxonomicComparison: builder.query<TaxonomicComparisonQueryResult, TaxonomicComparisonQueryParams>({
            query: params => ({
                body: taxonomicComparisonQuery,
                variables: params,
            }),
        }),
        communityComparisonChartData: builder.query<CommunityComparisonChartDataQueryResult, GenericQueryParems>({
            query: params => ({
                body: communityComparisonChartDataQuery,
                variables: params,
            }),
            transformResponse: (response: CommunityComparisonChartDataQueryResult) => {
                if (!response?.communityComparisonChartData?.data) {
                    return response;
                }

                //Flatten communityComparisonChartData years data to support multi year events
                const multiYearData = response.communityComparisonChartData.data
                    .flatMap(item => item.years)
                    .sort((a, b) => Number(a.samplingYear) - Number(b.samplingYear));

                return {
                    communityComparisonChartData: {
                        ...response.communityComparisonChartData,
                        data: [
                            {
                                metric: response.communityComparisonChartData.data[0].metric,
                                years: multiYearData,
                            },
                        ],
                    },
                };
            },
        }),
        communityComparisonSamples: builder.query<CommunityComparisonSamplesQueryResult, CommunityComparisonSamplesQueryParams>({
            query: params => ({
                body: communityComparisonSamplesQuery,
                variables: params,
            }),
        }),
        communityComparisonMarker: builder.query<communityComparisonMarkersQueryResult, CommunityComparisonMarkerQueryParams>({
            query: params => ({
                body: communityComparisonMarkerQuery,
                variables: params,
            }),
        }),
        sampleComparison: builder.query<SampleComparisonQueryResult, SampleComparisonQueryParams>({
            query: params => ({
                body: sampleComparisonQuery,
                variables: params,
            }),
        }),
        habitatTrends: builder.query<HabitatTrendsQueryResult, HabitatTrendsQueryParams>({
            query: params => ({
                body: habitatTrendsQuery,
                variables: { ...params, hlcaCharts: params.habitatInsightsCharts },
            }),
        }),
    }),
});

export const {
    useStatisticalComparisonQuery,
    useSpeciesTrendsQuery,
    useTaxonomicComparisonQuery,
    useCommunityComparisonChartDataQuery,
    useCommunityComparisonSamplesQuery,
    useCommunityComparisonMarkerQuery,
    useSampleComparisonQuery,
    useHabitatTrendsQuery,
} = ecologyHubGraphSlice;

export default ecologyHubGraphSlice.reducer;
